import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostBinding, computed } from '@angular/core';

import { VisualizationBetState, VisualizationConstants, betStateMapping } from '../../models/my-bets-visualization-types';
import { MyBetsVisualizationService } from '../../services/my-bets-visualization.service';
import { VisualizationBaseComponent } from './visualization-base.component';

export interface StatOptions {
    currentStat: string;
    progressBarWidth: string;
    showTargetStat: boolean;
    betStateClassName: string;
}

export interface PlayerStatContext {
    currentStat: number;
    isPrimaryPlayer: boolean;
    isBelowTargetStat: boolean;
    isSinglePlayerWon: boolean;
    isDoublePlayerWon: boolean;
    visualizationState: VisualizationBetState;
}

@Component({
    imports: [NgClass],
    selector: 'ms-my-bets-h2h-visualization',
    templateUrl: 'my-bets-h2h-visualization.component.html',
    styleUrl: './my-bets-h2h-visualization.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyBetsH2HVisualizationComponent extends VisualizationBaseComponent {
    @HostBinding('class') className = 'live-bets-tracking';

    private readonly playerWonValue = '100%';
    private readonly playerLostValue = '50%';
    private readonly neitherMarketPlayersCount = 2;
    private readonly neitherMarketOptionsCount = 3;

    constructor(private myBetsVisualizationService: MyBetsVisualizationService) {
        super();
    }

    targetStatValue = computed(() => this.statsData().targetStat[0]);

    stats = computed(() => [
        this.getStatOptions(this.statsData().player1CurrStat, this.isHomePlayer()),
        this.getStatOptions(this.statsData().player2CurrStat, this.isAwayPlayer()),
    ]);

    private participants = computed(() => this.myBetsVisualizationService.getParticipants(this.event().participants));
    homePlayer = computed(() => this.participants().participant1 || '');
    awayPlayer = computed(() => this.participants().participant2 || '');

    private isNeitherMarket = computed(() => this.checkIfNeitherMarket());
    private isBelowTargetStat = computed(
        () => this.statsData().player1CurrStat < this.targetStatValue() && this.statsData().player2CurrStat < this.targetStatValue(),
    );

    private isDoublePlayerWon = computed(
        () => this.statsData().player1CurrStat >= this.targetStatValue() && this.statsData().player2CurrStat >= this.targetStatValue(),
    );

    private isHomePlayer(): boolean {
        return this.statsData().player.playerName === VisualizationConstants.HomePlayer;
    }

    private isAwayPlayer(): boolean {
        return this.statsData().player.playerName === VisualizationConstants.AwayPlayer;
    }

    private checkIfNeitherMarket(): boolean {
        const stats = this.myBetsVisualizationService.marketStatsDetails(this.event(), this.bet().marketId);
        const playerCount = stats.marketStatsInfo?.options.reduce((counter, x) => (x.playerId ? counter + 1 : counter), 0);

        return stats.marketStatsInfo?.options.length === this.neitherMarketOptionsCount && playerCount === this.neitherMarketPlayersCount;
    }

    private getStatOptions(currentStat: number, isPrimaryPlayer: boolean): StatOptions {
        const context: PlayerStatContext = {
            currentStat,
            isPrimaryPlayer,
            isBelowTargetStat: this.isBelowTargetStat(),
            isSinglePlayerWon: this.isSinglePlayerWon(currentStat),
            isDoublePlayerWon: this.isDoublePlayerWon(),
            visualizationState: this.getVisualizationState(),
        };

        return this.isNeitherMarket() ? this.getNeitherMarketStatOptions(context) : this.getRegularMarketStatOptions(context);
    }

    private getNeitherMarketStatOptions(context: PlayerStatContext): StatOptions {
        if (this.isCashedOut() || context.visualizationState !== VisualizationBetState.Cancelled) {
            return this.getStatOptionBasedOnPlayer(context);
        }

        return this.getStatOptionsBasedOnBetState(context.isPrimaryPlayer, context.visualizationState);
    }

    private getRegularMarketStatOptions(context: PlayerStatContext): StatOptions {
        if (this.isCashedOut() || context.visualizationState === VisualizationBetState.Open) {
            return this.getStatOptionBasedOnPlayer(context);
        }

        return this.getStatOptionsBasedOnBetState(context.isPrimaryPlayer, context.visualizationState);
    }

    private isSinglePlayerWon(currentStat: number): boolean {
        return currentStat >= this.statsData().player1CurrStat && currentStat >= this.statsData().player2CurrStat;
    }

    private getStatOptionBasedOnPlayer(context: PlayerStatContext): StatOptions {
        if (context.isBelowTargetStat) {
            return this.getStatOptionsForPlayerInProgress(context.currentStat, context.isPrimaryPlayer);
        }

        return (context.isSinglePlayerWon && !context.isDoublePlayerWon) || (context.isDoublePlayerWon && context.isPrimaryPlayer)
            ? this.getStatOptionsForPlayerWon(context.isPrimaryPlayer)
            : this.getStatOptionsForPlayerLost(context.isPrimaryPlayer);
    }

    private getStatOptionsBasedOnBetState(isPrimaryPlayer: boolean, visualizationState: VisualizationBetState): StatOptions {
        switch (visualizationState) {
            case VisualizationBetState.Won:
                return isPrimaryPlayer ? this.getStatOptionsForPlayerWon(isPrimaryPlayer) : this.getStatOptionsForPlayerLost(isPrimaryPlayer);
            case VisualizationBetState.Lost:
                return isPrimaryPlayer ? this.getStatOptionsForPlayerLost(isPrimaryPlayer) : this.getStatOptionsForPlayerWon(isPrimaryPlayer);
            case VisualizationBetState.Cancelled:
                return this.getStatOptionsForPlayerLost(isPrimaryPlayer);
            default:
                return this.getDefaultStatOptions();
        }
    }

    private getDefaultStatOptions(): StatOptions {
        return {
            currentStat: '',
            showTargetStat: false,
            progressBarWidth: '',
            betStateClassName: '',
        };
    }

    private getStatOptionsForPlayerInProgress(progressStat: number, isPrimaryPlayer: boolean): StatOptions {
        return {
            currentStat: progressStat.toString(),
            showTargetStat: true,
            progressBarWidth: this.calculateProgressBarWidth(progressStat),
            betStateClassName: this.getBetStateClassName(isPrimaryPlayer),
        };
    }

    private getStatOptionsForPlayerWon(isPrimaryPlayer: boolean): StatOptions {
        return {
            currentStat: this.targetStatValue().toString(),
            showTargetStat: false,
            progressBarWidth: this.playerWonValue,
            betStateClassName: this.getBetStateClassName(isPrimaryPlayer),
        };
    }

    private getStatOptionsForPlayerLost(isPrimaryPlayer: boolean): StatOptions {
        return {
            currentStat: '-',
            showTargetStat: true,
            progressBarWidth: this.playerLostValue,
            betStateClassName: this.getBetStateClassName(isPrimaryPlayer),
        };
    }

    private calculateProgressBarWidth(progressStat: number): string {
        return `calc((100% / ${this.targetStatValue()}) * ${progressStat})`;
    }

    private getBetStateClassName(isPrimaryPlayer: boolean): string {
        const totalCurrStat = this.statsData().player1CurrStat + this.statsData().player2CurrStat;
        const isSettled = !isPrimaryPlayer;

        return this.myBetsVisualizationService.getBetStateClassName(
            this.betMarketInfo().state,
            totalCurrStat,
            this.isCashedOut(),
            isSettled,
            this.isNeitherMarket(),
            this.statsData().player.playerId,
        );
    }

    private getVisualizationState(): VisualizationBetState {
        const { state } = this.betMarketInfo();

        return betStateMapping[state];
    }
}
